import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export type FollowUpStatus = 'open' | 'completed'

/** A follow-up row as returned by the backend. The fields after the base
 *  ``follow_ups`` columns are populated by the LEFT JOINs in
 *  ``database.py::_follow_up_select`` — extension/location (assignment display),
 *  users (assigned_display_name), and calls on ``call_id`` (recording_url +
 *  call_summary + duration_s). Any of the joined fields can be null when the
 *  assignment is unset or the call has not been analyzed yet. */
export interface FollowUp {
  id: string
  org_id: string
  call_id: string | null
  requester_name: string
  requester_phone: string
  task: string
  ai_suggestion: string | null
  /** What the caller spoke ("Dr. Smith", "billing") before extension match. */
  target_label: string | null
  assigned_extension_id: string | null
  assigned_user_id: string | null
  status: FollowUpStatus
  completed_by: string | null
  completed_at: number | null
  created_at: number
  updated_at: number

  // JOINed assignment display (org_extensions + org_locations)
  assigned_extension_label: string | null
  assigned_extension: string | null
  assigned_extension_kind: 'person' | 'department' | null
  assigned_location_label: string | null
  assigned_location_main_phone: string | null
  // JOINed user (assigned_user_id → users.display_name)
  assigned_display_name: string | null
  // JOINed call (call_id → calls)
  recording_url: string | null
  call_summary: string | null
  duration_s: number | null
}

interface ListResponse {
  follow_ups: FollowUp[]
}

interface DetailResponse {
  follow_up: FollowUp
}

export interface CreateFollowUpPayload {
  task: string
  requester_phone: string
  requester_name?: string
  ai_suggestion?: string
  target_label?: string
  assigned_extension_id?: string | null
  call_id?: string | null
}

export interface FollowUpFilters {
  status?: FollowUpStatus
  /** ``me`` resolves server-side to the authenticated user; or a raw user id. */
  assignee?: string
  /** Filter by a specific assigned extension id. */
  extension?: string
}

export function useFollowUps(filters: FollowUpFilters = {}) {
  const qs = new URLSearchParams()
  if (filters.status) qs.set('status', filters.status)
  if (filters.assignee) qs.set('assignee', filters.assignee)
  if (filters.extension) qs.set('extension', filters.extension)
  const query = qs.toString()

  return useQuery({
    queryKey: ['follow-ups', filters],
    queryFn: () =>
      apiFetch<ListResponse>(`/follow-ups${query ? `?${query}` : ''}`),
    // New follow-ups land mid-call from the inbound router's tool calls; keep
    // the queue fresh at the same cadence the appointments queue used.
    refetchInterval: 10_000,
  })
}

export function useFollowUp(id: string | undefined) {
  return useQuery({
    queryKey: ['follow-up', id],
    queryFn: () => apiFetch<DetailResponse>(`/follow-ups/${id}`),
    enabled: !!id,
  })
}

export function useCreateFollowUp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateFollowUpPayload) =>
      apiFetch<DetailResponse>('/follow-ups', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] })
    },
  })
}

export function useUpdateFollowUpStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: FollowUpStatus }) =>
      apiFetch<DetailResponse>(`/follow-ups/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['follow-up', variables.id] })
    },
  })
}

export function useReassignFollowUp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      assigned_extension_id,
    }: {
      id: string
      assigned_extension_id: string | null
    }) =>
      apiFetch<DetailResponse>(`/follow-ups/${id}/reassign`, {
        method: 'POST',
        body: JSON.stringify({ assigned_extension_id }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['follow-up', variables.id] })
    },
  })
}
